
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FeedService } from '../../services/feedService';
import { SocialService } from '../../services/socialService';
import { getCollectionProgress } from '../../services/collectionsService';
import { unlockFraction } from '@aluvo/constants/src/collectionCompletion';
import { getMyProfile, getMyCoins } from '../../services/authService';
import NextUpCard from '../../components/NextUpCard/NextUpCard';
import LandingPage from './landing/LandingPage';
import HomeIdentityBar from '../../components/HomeIdentityBar/HomeIdentityBar';
import MomentumStrip from '../../components/MomentumStrip/MomentumStrip';
import HomeFeed from '../../components/HomeFeed/HomeFeed';
import CollectionTensionBar from '../../components/CollectionTensionBar/CollectionTensionBar';
import TrendingVerification from '../../components/TrendingVerification/TrendingVerification';
import ContinueCollection from '../../components/ContinueCollection/ContinueCollection';
import CollectionProgressModule from '../../components/CollectionProgressModule/CollectionProgressModule';
import CollectorsToFollow from '../../components/CollectorsToFollow/CollectorsToFollow';
import styles from './Home.module.css';


function Home() {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [profile,     setProfile]     = useState(null);
    const [coins,       setCoins]       = useState([]);
    // For You meta: followingCount drives the follow-nobody modules below the feed.
    const [feedMeta,    setFeedMeta]    = useState(null);
    const [momentum,    setMomentum]    = useState(null);
    const [collections, setCollections] = useState([]);
    const [trending,    setTrending]    = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [collectors,  setCollectors]  = useState([]);
    const [loading,     setLoading]     = useState(true);

    const loadAll = useCallback(async () => {
        if (!user) return;
        try {
            const [
                profileData, coinsData, momentumData,
                collectionsData, trendingData, suggestionsData, collectorsData,
            ] = await Promise.allSettled([
                getMyProfile(),
                getMyCoins(),
                FeedService.getMomentum(),
                getCollectionProgress(),
                FeedService.getTrending(),
                FeedService.getOnboardingSuggestions(),
                SocialService.getUsers(),
            ]);

            if (profileData.status     === 'fulfilled') setProfile(profileData.value);
            if (coinsData.status       === 'fulfilled') setCoins(Array.isArray(coinsData.value) ? coinsData.value : []);
            if (momentumData.status    === 'fulfilled') setMomentum(momentumData.value);
            if (collectionsData.status === 'fulfilled') setCollections(collectionsData.value ?? []);
            if (trendingData.status    === 'fulfilled') setTrending(trendingData.value ?? []);
            if (suggestionsData.status === 'fulfilled') {
                const raw = suggestionsData.value?.suggestions ?? suggestionsData.value ?? [];
                setSuggestions(raw.slice(0, 4));
            }
            if (collectorsData.status === 'fulfilled') {
                const uid   = user?.id ?? user?.userId;
                const users = (collectorsData.value ?? []).filter(u => u.id !== uid).slice(0, 10);
                setCollectors(users);
            }
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (user) { loadAll(); } else { setLoading(false); }
    }, [user, loadAll]);

    if (!user) {
        return <LandingPage />;
    }

    // Follows nobody yet: show the existing starter modules (incl. people to follow) under the feed.
    const isNewUser = !loading && feedMeta !== null && !feedMeta.followingCount;
    // Zero-coin activation: one specific, quiz-ready recommendation near the top.
    const coinCount = profile?.stats?.badgeCount ?? profile?.coin_count ?? coins.length;
    const isZeroCoin = !loading && Number(coinCount) === 0;

    // Collection closest to completing (highest fill %)
    const closestCollection = (() => {
        // canEarnReward (server-computed) is false for an upcoming/ended seasonal collection.
        const incomplete = collections.filter(c => !c.completed && c.total > 0 && c.canEarnReward !== false);
        if (!incomplete.length) return null;
        // Fraction toward UNLOCKING (any-N-of-M collections measure against N).
        return incomplete.reduce((best, c) => (unlockFraction(c) > unlockFraction(best) ? c : best));
    })();

    // Only trending rows that have a real title AND posterUrl
    const validTrending = trending.filter(t => t.title && t.posterUrl);

    return (
        <main className={styles.loggedInPage}>
            {/* ── 1. Identity Bar ── */}
            <HomeIdentityBar profile={profile} coins={coins} loading={loading} />

            <div className={styles.mainLayout}>
                {/* ── CENTER FEED ── */}
                <div className={styles.centerColumn}>
                    {/* ── 1b. Zero-coin Next Up: direct path to the first quiz ── */}
                    {isZeroCoin && <NextUpCard />}

                    {/* ── 2. Momentum Strip ── */}
                    <MomentumStrip
                        momentum={momentum}
                        loading={loading}
                        onCardPress={(card) => {
                            if (card.media) navigate(`/movie/${card.media.kind}/${card.media.tmdbId}`);
                            if (card.collectionSlug) navigate(`/collections/${card.collectionSlug}`);
                        }}
                    />

                    {/* ── 3. Home feed: For You / Following (docs/home-feed.md) ── */}
                    <HomeFeed myCoins={coins} onMeta={setFeedMeta} />

                    {/* ── New-user modules: only rendered when feed is empty and data exists ── */}
                    {isNewUser && suggestions.length > 0 && (
                        <ContinueCollection
                            suggestions={suggestions}
                            onEarnCoin={(s) => navigate(`/movie/${s.kind ?? s.mediaType ?? 'movie'}/${s.tmdbId ?? s.id}`)}
                        />
                    )}

                    {isNewUser && closestCollection && (
                        <CollectionProgressModule
                            collection={closestCollection}
                            onVerifyNext={(item) => navigate(`/movie/${item.kind ?? 'movie'}/${item.tmdbId}`)}
                            onViewCollection={(slug) => navigate(`/collections/${slug}`)}
                        />
                    )}

                    {isNewUser && collectors.length > 0 && (
                        <CollectorsToFollow
                            collectors={collectors}
                            currentUserId={user?.id ?? user?.userId}
                            onViewProfile={(username) => navigate(`/user/${username}`)}
                        />
                    )}
                </div>

                {/* ── RIGHT SIDEBAR ── */}
                <aside className={styles.rightSidebar}>
                    {/* ── 4. Collection Tension Bar ── */}
                    <CollectionTensionBar
                        collections={collections}
                        loading={loading}
                        onCollectionClick={(slug) => navigate(`/collections/${slug}`)}
                    />

                    {/* ── 5. Trending — loading skeleton OR valid rows only (hidden when empty) ── */}
                    {loading && (
                        <TrendingVerification trending={[]} loading={true} onVerify={() => {}} />
                    )}
                    {!loading && validTrending.length > 0 && (
                        <TrendingVerification
                            trending={validTrending}
                            loading={false}
                            onVerify={(media) => navigate(`/movie/${media.kind}/${media.tmdbId}`)}
                        />
                    )}
                </aside>
            </div>
        </main>
    );
}

export default Home;